import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Brain, Zap, Shield, Sparkles } from "lucide-react";
import axios from "axios";
import { API } from "@/App";
import { toast } from "sonner";

const AnalysisLoading = ({ visitorId, visitorData, questionAnswers, onComplete }) => {
  const [progress, setProgress] = useState(0);
  const [phase, setPhase] = useState(0);
  const [analysisText, setAnalysisText] = useState("");

  const phases = [
    { text: "Scanning neural patterns...", icon: Brain },
    { text: "Analyzing personality matrix...", icon: Sparkles },
    { text: "Calculating numerology...", icon: Zap },
    { text: "Generating superhero identity...", icon: Shield }
  ];

  const codeLines = [
    `> INITIATING HEROSCAN PROTOCOL...`,
    `> SUBJECT: ${visitorData?.name || "Unknown"}`,
    `> PROFESSION: ${visitorData?.profession || "Unknown"}`,
    `> ROLE: ${visitorData?.role || "Unknown"}`,
    `> EXPERIENCE: ${visitorData?.experience || 0} YEARS`,
    `> PROCESSING ${questionAnswers?.length || 6} PERSONALITY ANSWERS...`,
    `> ANALYZING PERSONALITY VECTORS...`,
    `> CROSS-REFERENCING ARCHETYPE DATABASE...`,
    `> COMPUTING LIFE PATH NUMBER...`,
    `> CALCULATING EXPRESSION NUMBER...`,
    `> GENERATING UNIQUE SUPERHERO IDENTITY...`,
    `> COMPILING STRENGTHS AND ABILITIES...`,
    `> IDENTIFYING GROWTH OPPORTUNITIES...`,
    `> CRAFTING SUPERHERO TITLE...`,
    `> FINALIZING ANALYSIS...`
  ];

  useEffect(() => {
    // Animate progress
    const progressInterval = setInterval(() => {
      setProgress((prev) => {
        if (prev >= 95) {
          clearInterval(progressInterval);
          return 95;
        }
        return prev + Math.random() * 3;
      });
    }, 150);

    // Animate phases
    const phaseInterval = setInterval(() => {
      setPhase((prev) => (prev + 1) % phases.length);
    }, 2000);

    // Animate code text
    let lineIndex = 0;
    const textInterval = setInterval(() => {
      if (lineIndex < codeLines.length) {
        setAnalysisText((prev) => prev + codeLines[lineIndex] + "\n");
        lineIndex++;
      }
    }, 600);

    // Call the analysis API
    const runAnalysis = async () => {
      try {
        const response = await axios.post(`${API}/analyze`, {
          visitor_id: visitorId,
          question_answers: questionAnswers || []
        });

        // Complete progress
        setProgress(100);

        // Small delay for visual effect
        setTimeout(() => {
          onComplete(response.data);
        }, 500);
      } catch (error) {
        console.error("Analysis failed:", error);
        toast.error("Analysis failed. Please try again.");
        setProgress(0);
      }
    };

    runAnalysis();

    return () => {
      clearInterval(progressInterval);
      clearInterval(phaseInterval);
      clearInterval(textInterval);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visitorId]);

  const currentPhase = phases[phase];
  const PhaseIcon = currentPhase.icon;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="min-h-screen flex flex-col items-center justify-center relative px-6 overflow-hidden"
      data-testid="analysis-loading"
    >
      {/* Background effects */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-neon-purple/10 rounded-full blur-[150px] animate-pulse" />
        {/* Scanning lines */}
        <div className="absolute inset-0">
          {[...Array(5)].map((_, i) => (
            <div
              key={i}
              className="absolute left-0 right-0 h-px bg-gradient-to-r from-transparent via-neon-cyan/30 to-transparent"
              style={{
                top: `${20 + i * 15}%`,
                animationDelay: `${i * 0.2}s`,
                animation: "scan-line 3s linear infinite"
              }}
            />
          ))}
        </div>
      </div>

      <div className="relative z-10 w-full max-w-2xl mx-auto text-center">
        {/* Animated icon */}
        <motion.div
          animate={{ rotate: 360 }}
          transition={{ duration: 3, repeat: Infinity, ease: "linear" }}
          className="w-24 h-24 mx-auto mb-8 relative"
        >
          <div className="absolute inset-0 rounded-full border-2 border-neon-purple/30 animate-ping" />
          <div className="absolute inset-2 rounded-full border border-neon-cyan/50" />
          <div className="absolute inset-4 rounded-full bg-gradient-to-br from-neon-purple/20 to-neon-cyan/20 flex items-center justify-center">
            <PhaseIcon className="w-10 h-10 text-neon-purple" />
          </div>
        </motion.div>

        {/* Phase text */}
        <motion.h2
          key={phase}
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -10 }}
          className="font-orbitron text-xl sm:text-2xl font-bold text-white mb-4 tracking-wider"
        >
          {currentPhase.text}
        </motion.h2>

        {/* Progress bar */}
        <div className="w-full max-w-md mx-auto mb-8">
          <div className="h-1 bg-white/10 rounded-full overflow-hidden">
            <motion.div
              initial={{ width: 0 }}
              animate={{ width: `${progress}%` }}
              className="h-full bg-gradient-to-r from-neon-purple to-neon-cyan shadow-[0_0_10px_rgba(127,90,240,0.5)]"
            />
          </div>
          <div className="flex justify-between mt-2 text-white/40 font-rajdhani text-sm">
            <span>Analyzing</span>
            <span>{Math.round(progress)}%</span>
          </div>
        </div>

        {/* Code terminal effect */}
        <div className="glass rounded-lg p-4 text-left max-h-48 overflow-hidden">
          <div className="flex items-center gap-2 mb-3 border-b border-white/10 pb-2">
            <div className="w-3 h-3 rounded-full bg-red-500/50" />
            <div className="w-3 h-3 rounded-full bg-yellow-500/50" />
            <div className="w-3 h-3 rounded-full bg-green-500/50" />
            <span className="ml-2 text-white/40 text-xs font-mono">HEROSCAN TERMINAL</span>
          </div>
          <pre className="font-mono text-xs text-neon-cyan/80 whitespace-pre-wrap">
            {analysisText}
            <span className="animate-pulse">_</span>
          </pre>
        </div>

        {/* Visitor info */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1 }}
          className="mt-8 flex items-center justify-center gap-4 text-white/40 font-rajdhani"
        >
          <span>Subject: {visitorData?.name}</span>
          <span className="w-1 h-1 rounded-full bg-white/40" />
          <span>{visitorData?.profession}</span>
        </motion.div>
      </div>
    </motion.div>
  );
};

export default AnalysisLoading;
